import {
  Aperture,
  CircleDot,
  LogIn,
  Mic,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  PlayCircle,
  Power,
  RotateCcw,
  Send,
  Settings,
  SkipForward,
  Sparkles,
  Waves
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_REALTIME_ROUTE,
  DEFAULT_REALTIME_VOICE,
  REALTIME_ROUTE_OPTIONS,
  getRealtimeRouteForVoice,
  getRealtimeVoiceConfig,
  getRealtimeVoicesForRoute,
  isRealtimeVoice,
  resolveRealtimeRouteVoice,
  type RealtimeRoute,
  type RealtimeVoice
} from "../shared/realtime";
import { DEFAULT_INITIAL_IMAGE_URL } from "../shared/game";
import {
  DEFAULT_IMAGE_PROVIDER,
  IMAGE_PROVIDER_OPTIONS,
  getImageProviderConfig,
  isImageProvider,
  resolveImageProvider,
  type ImageProvider
} from "../shared/imageProvider";
import {
  STORY_BEATS,
  STORY_TURNS_PER_BEAT,
  getStoryBeatIndex,
  getStoryBeatPrompt,
  type StoryBeat,
  type StoryVisualFocus,
  type StoryVisualMood
} from "../shared/story";
import { PcmPlayer, PcmRecorder } from "./lib/audio";
import { OmniCameraFrameSampler } from "./lib/omniCamera";
import {
  type MiniCpmRealtimeMode,
  type ServerToClientMessage,
  type TurnStageName,
  SessionSocket,
  createSession,
  getPublicConfig,
  login
} from "./lib/sessionClient";
import {
  NARRATION_TRANSCRIPT_STORAGE_KEY,
  parseNarrationTranscriptPreference,
  serializeNarrationTranscriptPreference
} from "./lib/transcriptPrefs";
import { resetAppViewportPosition, syncAppViewportHeight } from "./lib/viewport";

interface TranscriptLine {
  id: string;
  role: "assistant" | "user" | "system" | "narration";
  text: string;
}

interface SavedDefaultAuth {
  token: string;
  username: string;
  expiresAt: number;
}

const ROUTE_CONNECT_TIMEOUT_MS: Record<RealtimeRoute, number> = {
  openai: 20_000,
  xai: 20_000,
  "qwen-audio": 25_000,
  aliyun: 20_000,
  minicpm: 120_000
};

interface RewardVideo {
  beatIndex: number;
  title: string;
  videoUrl: string;
  playbackId: string;
}

type InputMode = "voice" | "text";
type ScenePhase = "idle" | "feedback" | "generating" | "transition";

interface TurnStageState {
  stage: TurnStageName;
  label: string;
  text: string;
  sceneTitle?: string;
}

interface SceneCameraState {
  focus: StoryVisualFocus;
  mood: StoryVisualMood;
}

interface NarrationCue {
  id: string;
  title: string;
  text: string;
  durationMs: number;
}

const AUTH_STORAGE_KEY = "whisperverse.defaultAuth.v1";
const MAX_TALK_HOLD_MS = 60_000;
const DEFAULT_STORY_PROGRESS = { beatIndex: 0, turnsInBeat: 0, turnsRequired: STORY_TURNS_PER_BEAT };
const DEFAULT_SCENE_CAMERA: SceneCameraState = { focus: "medium", mood: "observe" };
const SEED_TRANSCRIPT: TranscriptLine = {
  id: "seed",
  role: "system",
  text: "线路未接入。连接后，林夏会在超市后门接起这通电话。"
};

export function App() {
  const [savedAuthSeed] = useState(() => readSavedDefaultAuth());
  const [socketState, setSocketState] = useState("idle");
  const [realtimeReady, setRealtimeReady] = useState(false);
  const [status, setStatus] = useState(savedAuthSeed ? "请选择技术路线后连接会话" : "请先登录");
  const [authToken, setAuthToken] = useState(savedAuthSeed?.token ?? "");
  const [authExpiresAt, setAuthExpiresAt] = useState(savedAuthSeed?.expiresAt ?? 0);
  const [username, setUsername] = useState(savedAuthSeed?.username ?? "admin");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [route, setRoute] = useState<RealtimeRoute>(DEFAULT_REALTIME_ROUTE);
  const [voice, setVoice] = useState<RealtimeVoice>(DEFAULT_REALTIME_VOICE);
  const [imageProvider, setImageProvider] = useState<ImageProvider>(DEFAULT_IMAGE_PROVIDER);
  const [minicpmRealtimeMode, setMinicpmRealtimeMode] = useState<MiniCpmRealtimeMode>("audio");
  const [inputMode, setInputMode] = useState<InputMode>("voice");
  const [textInput, setTextInput] = useState("");
  const [sceneImage, setSceneImage] = useState(DEFAULT_INITIAL_IMAGE_URL);
  const [sceneTitle, setSceneTitle] = useState("超市后门吸烟点");
  const [visualSceneTitle, setVisualSceneTitle] = useState("超市后门吸烟点");
  const [storyProgress, setStoryProgress] = useState(DEFAULT_STORY_PROGRESS);
  const [isTalking, setIsTalking] = useState(false);
  const [isPreparingToTalk, setIsPreparingToTalk] = useState(false);
  const [isDuplexListening, setIsDuplexListening] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);
  const [inputLockReason, setInputLockReason] = useState("");
  const [assistantSpeaking, setAssistantSpeaking] = useState(false);
  const [liveAssistantTranscript, setLiveAssistantTranscript] = useState("");
  const [choicePrompt, setChoicePrompt] = useState("");
  const [directorFeedback, setDirectorFeedback] = useState("");
  const [turnStage, setTurnStage] = useState<TurnStageState | undefined>(undefined);
  const [narration, setNarration] = useState<NarrationCue | undefined>(undefined);
  const [showNarrationInTranscript, setShowNarrationInTranscript] = useState(() => readNarrationTranscriptPreference());
  const [scenePhase, setScenePhase] = useState<ScenePhase>("idle");
  const [sceneTransitionText, setSceneTransitionText] = useState("");
  const [sceneCamera, setSceneCamera] = useState<SceneCameraState>(DEFAULT_SCENE_CAMERA);
  const [rewardHint, setRewardHint] = useState<{ title: string; videoUrl: string } | undefined>(undefined);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [leftCollapsed, setLeftCollapsed] = useState(true);
  const [conversationCollapsed, setConversationCollapsed] = useState(true);
  const [activeRewardVideo, setActiveRewardVideo] = useState<RewardVideo | undefined>(undefined);
  const [unlockedRewardVideos, setUnlockedRewardVideos] = useState<Set<string>>(() => new Set([STORY_BEATS[0].videoUrl]));
  const [playedRewardVideos, setPlayedRewardVideos] = useState<Set<string>>(() => new Set());
  const [failedRewardVideos, setFailedRewardVideos] = useState<Set<string>>(() => new Set());
  const [transcript, setTranscript] = useState<TranscriptLine[]>([SEED_TRANSCRIPT]);

  const socketRef = useRef<SessionSocket | undefined>(undefined);
  const recorderRef = useRef(new PcmRecorder());
  const duplexRecorderRef = useRef(new PcmRecorder());
  const omniCameraRef = useRef(new OmniCameraFrameSampler());
  const playerRef = useRef<PcmPlayer | undefined>(undefined);
  const conversationPanelRef = useRef<HTMLElement | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const assistantDeltaRef = useRef("");
  const userDeltaRef = useRef("");
  const activeResponseGenerationRef = useRef<number | undefined>(undefined);
  const connectPromiseRef = useRef<Promise<boolean> | undefined>(undefined);
  const realtimeReadyRef = useRef(false);
  const playedRewardVideosRef = useRef<Set<string>>(new Set());
  const failedRewardVideosRef = useRef<Set<string>>(new Set());
  const talkIntentRef = useRef(false);
  const talkingRef = useRef(false);
  const minicpmDuplexListeningRef = useRef(false);
  const minicpmRealtimeModeRef = useRef<MiniCpmRealtimeMode>(minicpmRealtimeMode);
  const lastOmniFrameAtRef = useRef(0);
  const routeRef = useRef(route);
  const inputModeRef = useRef(inputMode);
  const inputLockedRef = useRef(false);
  const activeTalkPointerIdRef = useRef<number | undefined>(undefined);
  const talkHoldTimeoutRef = useRef<number | undefined>(undefined);
  const micPermissionStateRef = useRef<"idle" | "prompting" | "granted" | "denied">("idle");
  const micPermissionPromiseRef = useRef<Promise<boolean> | undefined>(undefined);
  const spaceDownRef = useRef(false);
  const connectionSeqRef = useRef(0);
  const assistantSpeakingTimerRef = useRef<number | undefined>(undefined);
  const rewardHintTimerRef = useRef<number | undefined>(undefined);
  const narrationQueueRef = useRef<NarrationCue[]>([]);
  const narrationActiveRef = useRef(false);
  const narrationTimerRef = useRef<number | undefined>(undefined);
  const showNarrationInTranscriptRef = useRef(showNarrationInTranscript);
  const scenePhaseTimerRef = useRef<number | undefined>(undefined);
  const narrowViewportRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    playerRef.current = new PcmPlayer();
    const micTimer = window.setTimeout(() => {
      if (inputMode === "voice") void ensureMicrophonePermission("auto");
    }, 700);
    return () => {
      window.clearTimeout(micTimer);
      window.clearTimeout(assistantSpeakingTimerRef.current);
      window.clearTimeout(rewardHintTimerRef.current);
      window.clearTimeout(narrationTimerRef.current);
      window.clearTimeout(scenePhaseTimerRef.current);
      window.clearTimeout(talkHoldTimeoutRef.current);
      recorderRef.current.stop();
      duplexRecorderRef.current.stop();
      omniCameraRef.current.stop();
      socketRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (route !== "minicpm" || inputMode !== "voice") stopMiniCpmDuplexMic();
  }, [route, inputMode]);

  useEffect(() => {
    inputLockedRef.current = inputLocked;
  }, [inputLocked]);

  useEffect(() => {
    routeRef.current = route;
  }, [route]);

  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);

  useEffect(() => {
    minicpmRealtimeModeRef.current = minicpmRealtimeMode;
  }, [minicpmRealtimeMode]);

  useEffect(() => {
    const finishTalkPointer = (event: PointerEvent) => {
      if (activeTalkPointerIdRef.current === undefined) return;
      if (event.pointerId !== activeTalkPointerIdRef.current) return;
      event.preventDefault();
      stopTalking();
    };
    const preserveTalkPointer = (event: PointerEvent) => {
      if (activeTalkPointerIdRef.current === undefined) return;
      if (event.pointerId !== activeTalkPointerIdRef.current) return;
      event.preventDefault();
      if (talkIntentRef.current || talkingRef.current) setStatus("继续按住说话，松开发送");
    };
    const finishTalkTouch = (event: TouchEvent) => {
      if (activeTalkPointerIdRef.current === undefined) return;
      event.preventDefault();
      stopTalking();
    };
    const forceStopTalk = () => {
      if (talkIntentRef.current || talkingRef.current) stopTalking();
    };
    const stopHiddenTalk = () => {
      if (document.visibilityState === "hidden") forceStopTalk();
    };

    window.addEventListener("pointerup", finishTalkPointer, true);
    window.addEventListener("pointercancel", preserveTalkPointer, true);
    window.addEventListener("touchend", finishTalkTouch, true);
    window.addEventListener("blur", forceStopTalk);
    window.addEventListener("pagehide", forceStopTalk);
    document.addEventListener("visibilitychange", stopHiddenTalk);
    return () => {
      window.removeEventListener("pointerup", finishTalkPointer, true);
      window.removeEventListener("pointercancel", preserveTalkPointer, true);
      window.removeEventListener("touchend", finishTalkTouch, true);
      window.removeEventListener("blur", forceStopTalk);
      window.removeEventListener("pagehide", forceStopTalk);
      document.removeEventListener("visibilitychange", stopHiddenTalk);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    void getPublicConfig()
      .then((config) => {
        if (disposed) return;
        if (isRealtimeVoice(config.realtimeVoice)) {
          setVoice((current) => (current === DEFAULT_REALTIME_VOICE ? config.realtimeVoice : current));
          setRoute((current) => (current === DEFAULT_REALTIME_ROUTE ? getRealtimeRouteForVoice(config.realtimeVoice) : current));
        }
        setImageProvider((current) => (current === DEFAULT_IMAGE_PROVIDER ? resolveImageProvider(config.imageProvider) : current));
        setMinicpmRealtimeMode(config.minicpmRealtimeMode === "video" ? "video" : "audio");
        if (config.defaultInitialImageUrl) setSceneImage(config.defaultInitialImageUrl);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
    };
  }, []);

  useEffect(() => {
    showNarrationInTranscriptRef.current = showNarrationInTranscript;
    writeNarrationTranscriptPreference(showNarrationInTranscript);
  }, [showNarrationInTranscript]);

  useEffect(() => {
    let frame: number | undefined;
    const settleTimers = new Set<number>();
    const media = window.matchMedia("(max-width: 720px)");
    const syncViewportLayout = () => {
      syncAppViewportHeight();
      resetAppViewportPosition();
      const narrow = isNarrowViewport();
      const wasNarrow = narrowViewportRef.current;
      if (narrow && wasNarrow !== true) {
        setLeftCollapsed(true);
        setConversationCollapsed(true);
      }
      narrowViewportRef.current = narrow;
    };
    const scheduleSync = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(syncViewportLayout);
    };
    const scheduleSettledSync = () => {
      scheduleSync();
      for (const delay of [120, 360, 720]) {
        const timer = window.setTimeout(() => {
          settleTimers.delete(timer);
          syncViewportLayout();
        }, delay);
        settleTimers.add(timer);
      }
    };

    syncViewportLayout();
    scheduleSettledSync();
    window.addEventListener("resize", scheduleSync);
    window.addEventListener("orientationchange", scheduleSync);
    window.addEventListener("pageshow", scheduleSync);
    window.addEventListener("focusout", scheduleSettledSync);
    media.addEventListener("change", scheduleSync);
    window.visualViewport?.addEventListener("resize", scheduleSync);
    window.visualViewport?.addEventListener("scroll", scheduleSync);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      for (const timer of settleTimers) window.clearTimeout(timer);
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("orientationchange", scheduleSync);
      window.removeEventListener("pageshow", scheduleSync);
      window.removeEventListener("focusout", scheduleSettledSync);
      media.removeEventListener("change", scheduleSync);
      window.visualViewport?.removeEventListener("resize", scheduleSync);
      window.visualViewport?.removeEventListener("scroll", scheduleSync);
    };
  }, []);

  useEffect(() => {
    const transcriptElement = transcriptRef.current;
    if (!transcriptElement) return;
    const scrollToBottom = () => {
      transcriptElement.scrollTop = transcriptElement.scrollHeight;
    };
    const frame = requestAnimationFrame(scrollToBottom);
    const timer = window.setTimeout(scrollToBottom, 50);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [transcript]);

  useEffect(() => {
    if (!authToken) return;
    if (!isSavedAuthActive(authExpiresAt)) {
      clearSavedDefaultAuth();
      setAuthToken("");
      setAuthExpiresAt(0);
      setStatus("登录授权已过期，请重新登录");
      return;
    }

    const timer = window.setInterval(() => {
      if (isSavedAuthActive(authExpiresAt)) return;
      clearSavedDefaultAuth();
      setAuthToken("");
      setAuthExpiresAt(0);
      setStatus("登录授权已过期，请重新登录");
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [authExpiresAt, authToken]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (
        inputMode !== "voice" ||
        event.code !== "Space" ||
        event.repeat ||
        settingsOpen ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      event.preventDefault();
      spaceDownRef.current = true;
      if (inputLockedRef.current) skipDirectorPerformance({ status: "已打断，正在听你说" });
      void startTalking({ allowLockedInterrupt: true });
    }

    function onKeyUp(event: KeyboardEvent) {
      if (event.code !== "Space" || !spaceDownRef.current) return;
      event.preventDefault();
      spaceDownRef.current = false;
      stopTalking();
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [inputLocked, inputMode, settingsOpen, socketState]);

  async function ensureSessionReady(connectingStatus = "正在连接会话") {
    if (socketRef.current && realtimeReadyRef.current) return true;
    if (connectPromiseRef.current) return connectPromiseRef.current;
    setStatus(connectingStatus);
    return connect();
  }

  async function ensureMicrophonePermission(trigger: "auto" | "gesture") {
    if (!navigator.mediaDevices?.getUserMedia) {
      setInputMode("text");
      if (trigger === "gesture") setConversationCollapsed(false);
      setStatus("当前浏览器无法访问麦克风，已切换到文字输入");
      return false;
    }
    if (micPermissionStateRef.current === "granted") return true;
    if (trigger === "auto" && micPermissionStateRef.current === "denied") return false;
    if (micPermissionPromiseRef.current) return micPermissionPromiseRef.current;

    micPermissionStateRef.current = "prompting";
    if (trigger === "auto") setStatus("正在申请麦克风权限");
    const promise = recorderRef.current
      .requestPermission()
      .then(() => {
        micPermissionStateRef.current = "granted";
        if (trigger === "auto") setStatus(savedAuthSeed ? "麦克风权限已就绪" : "麦克风权限已就绪，请先登录");
        return true;
      })
      .catch(() => {
        micPermissionStateRef.current = "denied";
        setInputMode("text");
        if (trigger === "gesture") setConversationCollapsed(false);
        setStatus("麦克风权限未开启，已切换到文字输入。可在浏览器设置中允许麦克风后再切回语音。");
        return false;
      })
      .finally(() => {
        if (micPermissionPromiseRef.current === promise) micPermissionPromiseRef.current = undefined;
      });
    micPermissionPromiseRef.current = promise;
    return promise;
  }

  async function connect(authTokenOverride = authToken): Promise<boolean> {
    if (connectPromiseRef.current) return connectPromiseRef.current;

    setStatus("正在连接会话");
    const promise = connectSession(authTokenOverride).catch((error) => {
      realtimeReadyRef.current = false;
      setRealtimeReady(false);
      setSocketState("error");
      setStatus(error instanceof Error ? error.message : "连接失败");
      return false;
    });
    connectPromiseRef.current = promise;
    try {
      const connected = await promise;
      if (connected) {
        setSettingsOpen(false);
        syncViewportAfterLogin();
        if (isNarrowViewport()) {
          setLeftCollapsed(true);
          setConversationCollapsed(true);
          narrowViewportRef.current = true;
        }
      }
      return connected;
    } finally {
      if (connectPromiseRef.current === promise) connectPromiseRef.current = undefined;
    }
  }

  function enterCallSurface() {
    setSettingsOpen(false);
    syncViewportAfterLogin();
    if (isNarrowViewport()) {
      setLeftCollapsed(true);
      setConversationCollapsed(true);
      narrowViewportRef.current = true;
    }
  }

  async function connectSession(authTokenOverride = authToken): Promise<boolean> {
    const effectiveAuthToken = authTokenOverride || authToken;
    const savedAuth = readSavedDefaultAuth();
    const effectiveAuthExpiresAt = savedAuth?.token === effectiveAuthToken ? savedAuth.expiresAt : authExpiresAt;
    if (!isSavedAuthActive(effectiveAuthExpiresAt)) {
      clearSavedDefaultAuth();
      setAuthToken("");
      setAuthExpiresAt(0);
      setSettingsOpen(true);
      setStatus("登录授权已过期，请重新登录");
      return false;
    }

    if (!effectiveAuthToken) {
      setSettingsOpen(true);
      setStatus("请先登录");
      return false;
    }

    stopMiniCpmDuplexMic();
    socketRef.current?.close();
    socketRef.current = undefined;
    realtimeReadyRef.current = false;
    setRealtimeReady(false);
    const connectionSeq = connectionSeqRef.current + 1;
    connectionSeqRef.current = connectionSeq;
    setSocketState("connecting");

    const session = await createSession();
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let sessionSocket: SessionSocket | undefined;
      const connectTimeoutMs = ROUTE_CONNECT_TIMEOUT_MS[route] ?? 20_000;
      const timeout = window.setTimeout(() => {
        realtimeReadyRef.current = false;
        setRealtimeReady(false);
        setSocketState("error");
        setStatus("语音链路连接超时，请重试");
        if (socketRef.current === sessionSocket) socketRef.current = undefined;
        sessionSocket?.close();
        if (effectiveAuthToken) setSettingsOpen(true);
        settle(false);
      }, connectTimeoutMs);
      const settle = (ready: boolean) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(ready);
      };
      const socket = new SessionSocket(
        session.sessionId,
        (message) => {
          if (connectionSeqRef.current !== connectionSeq) return;
          if (message.type === "status" && message.status === "realtime_ready") {
            realtimeReadyRef.current = true;
            setRealtimeReady(true);
            if (message.minicpmMode) {
              minicpmRealtimeModeRef.current = message.minicpmMode;
              setMinicpmRealtimeMode(message.minicpmMode);
            }
          }
          handleServerMessage(message);
          if (message.type === "status" && message.status === "realtime_ready") {
            if (routeRef.current === "minicpm" && message.realtimeMode === "duplex" && inputModeRef.current === "voice") {
              void startMiniCpmDuplexMic();
            }
            settle(true);
          }
          if (message.type === "status" && message.status === "realtime_closed") {
            realtimeReadyRef.current = false;
            setRealtimeReady(false);
          }
          if (message.type === "error") {
            realtimeReadyRef.current = false;
            setRealtimeReady(false);
            setSocketState("error");
            if (effectiveAuthToken) setSettingsOpen(true);
            settle(false);
          }
        },
        (state) => {
          if (connectionSeqRef.current !== connectionSeq) return;
          if (state !== "connected") {
            realtimeReadyRef.current = false;
            setRealtimeReady(false);
          }
          setSocketState(state);
          setStatus(state === "connected" ? "会话已连接" : state);
          if (state === "connected") {
            try {
              socket.send({
                type: "configure",
                authToken: effectiveAuthToken || undefined,
                voice,
                imageProvider
              });
              enterCallSurface();
              setStatus("会话已连接，正在接入语音链路");
            } catch (error) {
              setStatus(error instanceof Error ? error.message : "发送配置失败");
              setSocketState("error");
              if (effectiveAuthToken) setSettingsOpen(true);
              settle(false);
            }
          }
          if (state === "error" || state === "closed") {
            if (!settled) {
              if (state === "closed") setStatus("语音链路已关闭，请重试");
              if (effectiveAuthToken) setSettingsOpen(true);
            }
            settle(false);
          }
        }
      );
      sessionSocket = socket;
      socketRef.current = socket;
      socket.connect();
    });
  }

  async function handleLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoginError("");
    try {
      const result = await login(username, password);
      writeSavedDefaultAuth({ token: result.token, username, expiresAt: result.expiresAt });
      setAuthToken(result.token);
      setAuthExpiresAt(result.expiresAt);
      setPassword("");
      setLoginError("");
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      setSettingsOpen(true);
      syncViewportAfterLogin();
      if (isNarrowViewport()) {
        setLeftCollapsed(true);
        setConversationCollapsed(true);
        narrowViewportRef.current = true;
      }
      setStatus("登录授权已生效，请选择技术路线后连接会话");
      window.requestAnimationFrame(syncViewportAfterLogin);
      for (const delay of [120, 360, 720, 1200]) {
        window.setTimeout(syncViewportAfterLogin, delay);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "登录失败";
      setLoginError(message);
      setStatus(message);
    }
  }

  function syncViewportAfterLogin() {
    syncAppViewportHeight();
    resetAppViewportPosition();
  }

  function handleServerMessage(message: ServerToClientMessage) {
    switch (message.type) {
      case "status":
        setStatus(message.detail || message.status);
        if (message.status === "realtime_ready" && route === "minicpm") {
          setStatus(message.realtimeMode === "duplex" ? "MiniCPM 全双工语音已就绪，按住说话" : message.detail || "MiniCPM 已就绪");
        }
        if (message.status === "realtime_closed") stopMiniCpmDuplexMic();
        break;
      case "error":
        stopMiniCpmDuplexMic();
        setStatus(message.detail ? `${message.message}: ${message.detail}` : message.message);
        appendTranscript("system", message.message);
        break;
      case "choice.prompt":
        setChoicePrompt(message.text);
        break;
      case "input.locked":
        setInputLocked(true);
        setInputLockReason(message.reason);
        setChoicePrompt("");
        setStatus(message.reason);
        break;
      case "input.ready":
        setInputLocked(false);
        setInputLockReason("");
        setDirectorFeedback("");
        setTurnStage({ stage: "ready", label: "轮到你", text: message.prompt || "轮到你了。" });
        if (message.prompt) {
          setChoicePrompt(message.prompt);
          setStatus(message.prompt);
        } else {
          setStatus("轮到你了");
        }
        break;
      case "turn.stage":
        setTurnStage({ stage: message.stage, label: message.label, text: message.text, sceneTitle: message.sceneTitle });
        setStatus(message.text);
        break;
      case "narration.show":
        setTurnStage({ stage: "framing", label: message.title, text: message.text });
        enqueueNarration({
          id: crypto.randomUUID(),
          title: message.title,
          text: message.text,
          durationMs: message.durationMs ?? 4500
        });
        if (showNarrationInTranscriptRef.current) appendTranscript("narration", message.text);
        break;
      case "scene.transition":
        setSceneTitle(message.title);
        setVisualSceneTitle(message.title);
        showScenePhase("transition", message.detail ?? `镜头转向${formatBeatTitle(message.title)}`);
        break;
      case "scene.camera":
        setSceneCamera({ focus: message.focus, mood: message.mood });
        setTurnStage({ stage: "framing", label: message.label, text: message.detail });
        showScenePhase("transition", message.detail || message.label);
        break;
      case "director.feedback":
        setStatus(message.text);
        setInputLockReason(message.text);
        setDirectorFeedback(message.text);
        setTurnStage({ stage: "heard", label: message.label, text: message.text });
        showScenePhase("feedback", message.text);
        break;
      case "director.speaking":
        setInputLocked(true);
        setInputLockReason(message.reason);
        setTurnStage({ stage: "speaking", label: "林夏", text: "林夏正在说话" });
        setStatus(message.reason);
        break;
      case "director.skipped":
        setInputLocked(false);
        setInputLockReason("");
        setDirectorFeedback("");
        setTurnStage({ stage: "ready", label: "轮到你", text: "你可以接着说。" });
        setChoicePrompt("你可以接着说。");
        setStatus("已跳过，轮到你");
        clearNarration();
        clearScenePhase();
        setSceneCamera(DEFAULT_SCENE_CAMERA);
        break;
      case "response.started":
        activeResponseGenerationRef.current = message.generation;
        assistantDeltaRef.current = "";
        setLiveAssistantTranscript("");
        markAssistantSpeaking(1800);
        break;
      case "audio.delta":
        markAssistantSpeaking(900);
        void playAssistantAudio(message.audio, message.generation);
        break;
      case "transcript.delta":
        if (message.role === "assistant") {
          if (message.generation !== activeResponseGenerationRef.current) return;
          markAssistantSpeaking(1200);
          assistantDeltaRef.current += message.text;
          setLiveAssistantTranscript(assistantDeltaRef.current);
          upsertLiveAssistant(assistantDeltaRef.current);
        }
        if (message.role === "user") {
          const nextText = mergeLiveTranscriptDelta(userDeltaRef.current, message.text);
          userDeltaRef.current = nextText;
          upsertLiveUser(nextText);
        }
        break;
      case "transcript.done":
        if (message.role === "assistant" && message.generation !== activeResponseGenerationRef.current) return;
        if (message.role === "assistant") {
          assistantDeltaRef.current = "";
          setLiveAssistantTranscript(message.text);
          finishAssistantSpeaking(650);
        }
        if (message.role === "user") {
          userDeltaRef.current = "";
        }
        appendTranscript(message.role, message.text);
        break;
      case "image.queued":
        setTurnStage({
          stage: "visualizing",
          label: "场景图排队",
          text: message.sceneTitle ? `排队生成：${formatBeatTitle(message.sceneTitle)}` : "场景图排队中",
          sceneTitle: message.sceneTitle
        });
        setStatus(message.replaced ? "已更新待生成场景图像" : "场景图像已排队");
        showScenePhase("generating", message.sceneTitle ? `排队生成：${formatBeatTitle(message.sceneTitle)}` : "场景图排队中");
        break;
      case "image.pending":
        setTurnStage({
          stage: "visualizing",
          label: "生成画面",
          text: message.sceneTitle ? `正在生成：${formatBeatTitle(message.sceneTitle)}` : "场景图生成中",
          sceneTitle: message.sceneTitle
        });
        setStatus("正在生成场景图像");
        if (message.sceneTitle) setVisualSceneTitle(message.sceneTitle);
        showScenePhase("generating", message.sceneTitle ? `正在生成：${formatBeatTitle(message.sceneTitle)}` : "场景图生成中");
        break;
      case "image.error":
        setStatus(`${message.message}，已保留上一张画面`);
        showScenePhase("feedback", "场景图生成失败，已保留上一张画面");
        break;
      case "image.ready":
        setSceneImage(message.imageUrl);
        if (message.sceneTitle) setVisualSceneTitle(message.sceneTitle);
        setSceneCamera({ focus: "medium", mood: "reveal" });
        setTurnStage({
          stage: "framing",
          label: "场景图已切换",
          text: message.sceneTitle ? `镜头切到：${formatBeatTitle(message.sceneTitle)}` : "场景图已切换",
          sceneTitle: message.sceneTitle
        });
        setStatus(`场景图像已更新${message.provider ? ` (${message.provider})` : ""}`);
        showScenePhase("transition", "场景图已切换");
        break;
      case "story.progress":
        setStoryProgress({
          beatIndex: message.beatIndex,
          turnsInBeat: message.turnsInBeat,
          turnsRequired: message.turnsRequired
        });
        break;
      case "story.reward":
        setStatus(`已解锁视频：${formatBeatTitle(message.title)}。可在线索进度中手动播放。`);
        setUnlockedRewardVideos((current) => new Set(current).add(message.videoUrl));
        showRewardHint(message.title, message.videoUrl);
        break;
      case "world.state":
        hydrateWorldState(message.state);
        break;
    }
  }

  function clearTalkHoldState() {
    activeTalkPointerIdRef.current = undefined;
    window.clearTimeout(talkHoldTimeoutRef.current);
    talkHoldTimeoutRef.current = undefined;
  }

  function beginTalkPointer(pointerId: number) {
    activeTalkPointerIdRef.current = pointerId;
    window.clearTimeout(talkHoldTimeoutRef.current);
    talkHoldTimeoutRef.current = window.setTimeout(() => {
      if (!talkIntentRef.current && !talkingRef.current) return;
      setStatus("单次按住说话已到时限，已自动发送");
      stopTalking();
    }, MAX_TALK_HOLD_MS);
  }

  function handleTalkPointerDown(event: React.PointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    beginTalkPointer(event.pointerId);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Some mobile browsers can reject capture during transient layout changes.
    }
    if (inputLockedRef.current) skipDirectorPerformance({ status: "已打断，正在听你说" });
    void startTalking({ allowLockedInterrupt: true });
  }

  function handleTalkPointerUp(event: React.PointerEvent<HTMLButtonElement>) {
    if (activeTalkPointerIdRef.current !== undefined && event.pointerId !== activeTalkPointerIdRef.current) return;
    event.preventDefault();
    stopTalking();
  }

  function handleTalkPointerCancel(event: React.PointerEvent<HTMLButtonElement>) {
    if (activeTalkPointerIdRef.current !== undefined && event.pointerId !== activeTalkPointerIdRef.current) return;
    event.preventDefault();
    if (talkIntentRef.current || talkingRef.current) setStatus("继续按住说话，松开发送");
  }

  async function startMiniCpmDuplexMic() {
    if (routeRef.current !== "minicpm" || inputModeRef.current !== "voice") return;
    if (minicpmDuplexListeningRef.current) return;
    if (!socketRef.current || !realtimeReadyRef.current) return;

    const microphoneReady = await ensureMicrophonePermission("auto");
    if (
      !microphoneReady ||
      !socketRef.current ||
      !realtimeReadyRef.current ||
      routeRef.current !== "minicpm" ||
      inputModeRef.current !== "voice"
    ) {
      return;
    }

    try {
      let omniVideoReady = false;
      if (minicpmRealtimeModeRef.current === "video") {
        try {
          await omniCameraRef.current.start({
            video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
            audio: false
          });
          omniVideoReady = true;
        } catch {
          omniCameraRef.current.stop();
        }
      }
      await duplexRecorderRef.current.start((audio) => {
        if (!minicpmDuplexListeningRef.current || !socketRef.current || !realtimeReadyRef.current) return;
        try {
          const now = Date.now();
          const shouldCaptureFrame =
            minicpmRealtimeModeRef.current === "video" && now - lastOmniFrameAtRef.current >= 1_000;
          const frame = shouldCaptureFrame ? omniCameraRef.current.captureJpegBase64() : undefined;
          if (shouldCaptureFrame) lastOmniFrameAtRef.current = now;
          socketRef.current.send({
            type: "audio.append",
            audio,
            ...(frame ? { videoFrames: [frame], maxSliceNums: 1 } : {})
          });
        } catch {
          stopMiniCpmDuplexMic();
        }
      });
      minicpmDuplexListeningRef.current = true;
      setIsDuplexListening(true);
      setStatus(omniVideoReady ? "MiniCPM Omni 全双工监听中" : "MiniCPM 全双工监听中");
    } catch (error) {
      minicpmDuplexListeningRef.current = false;
      setIsDuplexListening(false);
      omniCameraRef.current.stop();
      setInputMode("text");
      setStatus(error instanceof Error ? error.message : "无法启动 MiniCPM 全双工麦克风，已切换到文字输入");
    }
  }

  function stopMiniCpmDuplexMic() {
    const wasListening = minicpmDuplexListeningRef.current;
    minicpmDuplexListeningRef.current = false;
    if (wasListening) setIsDuplexListening(false);
    duplexRecorderRef.current.stop();
    omniCameraRef.current.stop();
    lastOmniFrameAtRef.current = 0;
  }

  async function startTalking(options: { allowLockedInterrupt?: boolean } = {}) {
    if (talkingRef.current) return;
    if (inputLockedRef.current && !options.allowLockedInterrupt) {
      setStatus(inputLockReason || "林夏正在观察现场，稍等她说完");
      return;
    }

    if (route === "minicpm" && minicpmDuplexListeningRef.current && socketRef.current && realtimeReadyRef.current) {
      talkIntentRef.current = true;
      clearLiveUserTranscript();
      setChoicePrompt("");
      setDirectorFeedback("");
      setTurnStage(undefined);
      interruptAssistantOutput();
      try {
        socketRef.current.send({ type: "audio.interrupt" });
        await playerRef.current?.resume();
        talkingRef.current = true;
        setIsTalking(true);
        setStatus("全双工监听中，松开让林夏回应");
      } catch (error) {
        talkIntentRef.current = false;
        clearTalkHoldState();
        setStatus(error instanceof Error ? error.message : "MiniCPM 全双工链路未就绪");
      }
      return;
    }

    talkIntentRef.current = true;
    clearLiveUserTranscript();
    setChoicePrompt("");
    setDirectorFeedback("");
    setTurnStage(undefined);
    setIsPreparingToTalk(true);

    const microphoneReady = await ensureMicrophonePermission("gesture");
    if (!microphoneReady) {
      talkIntentRef.current = false;
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      return;
    }
    if (!talkIntentRef.current) {
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      setStatus("麦克风权限已就绪，按住说话开始通话");
      return;
    }

    const ready = await ensureSessionReady("正在连接会话，连接后开始录音");
    if (!ready) {
      talkIntentRef.current = false;
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      return;
    }
    if (!talkIntentRef.current) {
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      setStatus("会话已连接，按住说话开始通话");
      return;
    }
    if (!socketRef.current || !realtimeReadyRef.current) {
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      setStatus("语音链路尚未就绪，请稍后再试");
      return;
    }

    interruptAssistantOutput();
    try {
      socketRef.current.send({ type: "audio.interrupt" });
      await playerRef.current?.resume();
      if (!talkIntentRef.current) {
        clearTalkHoldState();
        setIsPreparingToTalk(false);
        setStatus("会话已连接，按住说话开始通话");
        return;
      }
      await recorderRef.current.start(
        (audio) => {
          if (talkingRef.current && talkIntentRef.current) socketRef.current?.send({ type: "audio.append", audio });
        },
        routeRef.current === "qwen-audio" ? 16_000 : 24_000
      );
      if (!talkIntentRef.current) {
        recorderRef.current.stop();
        clearTalkHoldState();
        setIsPreparingToTalk(false);
        setStatus("会话已连接，按住说话开始通话");
        return;
      }
      talkingRef.current = true;
      setIsPreparingToTalk(false);
      setIsTalking(true);
      setStatus("正在录音，松开发送");
    } catch (error) {
      talkingRef.current = false;
      talkIntentRef.current = false;
      clearTalkHoldState();
      setIsPreparingToTalk(false);
      setIsTalking(false);
      setStatus(error instanceof Error ? error.message : "无法启动麦克风");
    }
  }

  function stopTalking() {
    clearTalkHoldState();
    talkIntentRef.current = false;
    setIsPreparingToTalk(false);
    if (!talkingRef.current) return;
    if (route === "minicpm" && minicpmDuplexListeningRef.current) {
      talkingRef.current = false;
      setIsTalking(false);
      playerRef.current?.reset();
      socketRef.current?.send({ type: "audio.commit" });
      setStatus("已放开，林夏可以回应");
      return;
    }
    recorderRef.current.stop();
    talkingRef.current = false;
    setIsTalking(false);
    playerRef.current?.reset();
    socketRef.current?.send({ type: "audio.commit" });
  }

  async function sendText(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = textInput.trim();
    if (!text) return;
    if (inputLocked) {
      setStatus(inputLockReason || "林夏正在观察现场，稍等她说完");
      return;
    }
    if (!socketRef.current || !realtimeReadyRef.current) {
      const ready = await ensureSessionReady("正在连接会话，连接后发送文字");
      if (!ready || !socketRef.current || !realtimeReadyRef.current) return;
    }
    if (!socketRef.current) {
      setStatus("会话未连接，请稍后再试");
      return;
    }
    appendTranscript("user", text);
    setChoicePrompt("");
    setDirectorFeedback("");
    setTurnStage(undefined);
    interruptAssistantOutput();
    try {
      await playerRef.current?.resume();
      socketRef.current?.send({ type: "audio.interrupt" });
      socketRef.current?.send({ type: "text.send", text });
      setTextInput("");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "无法启动语音播放，请再发送一次");
    }
  }

  function redrawCurrentScene() {
    if (!socketRef.current || socketState !== "connected") {
      setStatus("请先连接会话，再重绘画面");
      return;
    }

    try {
      const scenePrompt = describeSceneForImage(sceneTitle);
      socketRef.current.send({
        type: "image.generate",
        prompt: `same adult Chinese woman named Lin Xia, rose-pink shoulder-length bob haircut with straight bangs, black cropped leather jacket, white dress or long white top, black choker, dark stockings, white ankle boots, phone in hand, alone in the current story beat: ${scenePrompt}, late-night supermarket back entrance staff break spot, gray concrete wall, yellow plastic produce crates, cardboard boxes, thin cold-air vapor, anime visual novel key art, no readable text`
      });
      setStatus("正在提交重绘请求");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "无法提交重绘请求");
    }
  }

  function appendTranscript(role: TranscriptLine["role"], text: string) {
    if (!text.trim()) return;
    setTranscript((lines) => [
      ...lines.filter((line) => line.id !== "assistant-live" && line.id !== "user-live"),
      { id: crypto.randomUUID(), role, text }
    ].slice(-18));
  }

  function upsertLiveAssistant(text: string) {
    setTranscript((lines) => {
      const withoutLive = lines.filter((line) => line.id !== "assistant-live");
      return [...withoutLive, { id: "assistant-live", role: "assistant" as const, text }].slice(-18);
    });
  }

  function upsertLiveUser(text: string) {
    if (!text.trim()) return;
    setTranscript((lines) => {
      const withoutLive = lines.filter((line) => line.id !== "user-live");
      return [...withoutLive, { id: "user-live", role: "user" as const, text }].slice(-18);
    });
  }

  function clearLiveUserTranscript() {
    userDeltaRef.current = "";
    setTranscript((lines) => lines.filter((line) => line.id !== "user-live"));
  }

  async function playAssistantAudio(audio: string, generation: number) {
    const player = playerRef.current;
    if (!player || talkingRef.current || generation !== activeResponseGenerationRef.current) return;
    await player.resume();
    if (talkingRef.current || generation !== activeResponseGenerationRef.current) return;
    player.play(audio);
  }

  function interruptAssistantOutput() {
    activeResponseGenerationRef.current = undefined;
    assistantDeltaRef.current = "";
    setLiveAssistantTranscript("");
    finishAssistantSpeaking(0);
    playerRef.current?.reset();
    setTranscript((lines) => lines.filter((line) => line.id !== "assistant-live"));
  }

  function releaseInputLockLocally(prompt = "你可以接着说。") {
    inputLockedRef.current = false;
    setInputLocked(false);
    setInputLockReason("");
    setDirectorFeedback("");
    setTurnStage({ stage: "ready", label: "轮到你", text: prompt });
    setChoicePrompt(prompt);
    setStatus(prompt);
  }

  function skipDirectorPerformance(options: { status?: string } = {}) {
    interruptAssistantOutput();
    clearNarration();
    clearScenePhase();
    releaseInputLockLocally(options.status || "已跳过，轮到你");
    try {
      socketRef.current?.send({ type: "director.skip" });
    } catch {
      releaseInputLockLocally("已跳过，轮到你");
    }
  }

  function markAssistantSpeaking(holdMs: number) {
    window.clearTimeout(assistantSpeakingTimerRef.current);
    setAssistantSpeaking(true);
    assistantSpeakingTimerRef.current = window.setTimeout(() => setAssistantSpeaking(false), holdMs);
  }

  function finishAssistantSpeaking(delayMs: number) {
    window.clearTimeout(assistantSpeakingTimerRef.current);
    assistantSpeakingTimerRef.current = window.setTimeout(() => setAssistantSpeaking(false), delayMs);
  }

  function markRewardVideoPlayed(videoUrl: string) {
    const next = new Set(playedRewardVideosRef.current).add(videoUrl);
    const failed = new Set(failedRewardVideosRef.current);
    failed.delete(videoUrl);
    playedRewardVideosRef.current = next;
    failedRewardVideosRef.current = failed;
    setPlayedRewardVideos(next);
    setFailedRewardVideos(failed);
    setActiveRewardVideo((current) => (current?.videoUrl === videoUrl ? undefined : current));
  }

  function markRewardVideoFailed(videoUrl: string) {
    const next = new Set(failedRewardVideosRef.current).add(videoUrl);
    failedRewardVideosRef.current = next;
    setFailedRewardVideos(next);
    setActiveRewardVideo((current) => (current?.videoUrl === videoUrl ? undefined : current));
    setStatus("奖励视频加载失败，可点击线索重试");
  }

  function replayRewardVideo(beat: StoryBeat, index: number) {
    if (!beat.videoUrl) return;
    hideRewardHint();
    setActiveRewardVideo({
      beatIndex: index,
      title: beat.title,
      videoUrl: beat.videoUrl,
      playbackId: crypto.randomUUID()
    });
    setStatus(`正在回放：${formatBeatTitle(beat.title)}`);
  }

  function showRewardHint(title: string, videoUrl: string) {
    window.clearTimeout(rewardHintTimerRef.current);
    setRewardHint({ title, videoUrl });
    rewardHintTimerRef.current = window.setTimeout(() => setRewardHint(undefined), 16000);
  }

  function hideRewardHint() {
    window.clearTimeout(rewardHintTimerRef.current);
    setRewardHint(undefined);
  }

  function enqueueNarration(cue: NarrationCue) {
    narrationQueueRef.current = [...narrationQueueRef.current, cue].slice(-4);
    if (!narrationActiveRef.current) showNextNarration();
  }

  function showNextNarration() {
    window.clearTimeout(narrationTimerRef.current);
    const next = narrationQueueRef.current.shift();
    if (!next) {
      narrationActiveRef.current = false;
      setNarration(undefined);
      return;
    }

    narrationActiveRef.current = true;
    setNarration(next);
    narrationTimerRef.current = window.setTimeout(showNextNarration, next.durationMs);
  }

  function clearNarration() {
    window.clearTimeout(narrationTimerRef.current);
    narrationQueueRef.current = [];
    narrationActiveRef.current = false;
    setNarration(undefined);
  }

  function showScenePhase(phase: ScenePhase, text: string) {
    window.clearTimeout(scenePhaseTimerRef.current);
    setScenePhase(phase);
    setSceneTransitionText(text);
    if (phase !== "generating") {
      scenePhaseTimerRef.current = window.setTimeout(() => {
        setScenePhase("idle");
        setSceneTransitionText("");
      }, 2400);
    }
  }

  function clearScenePhase() {
    window.clearTimeout(scenePhaseTimerRef.current);
    setScenePhase("idle");
    setSceneTransitionText("");
  }

  function hydrateWorldState(state: Record<string, unknown>) {
    if (typeof state.sceneTitle === "string") {
      setSceneTitle(state.sceneTitle);
      setVisualSceneTitle(state.sceneTitle);
    }
    if (isRealtimeVoice(state.voice)) setVoice(state.voice);
    if (isImageProvider(state.imageProvider)) setImageProvider(state.imageProvider);
    const beatIndex = Number(state.storyBeat);
    const turnsInBeat = Number(state.storyTurnsInBeat);
    if (Number.isFinite(beatIndex)) {
      setStoryProgress({
        beatIndex,
        turnsInBeat: Number.isFinite(turnsInBeat) ? turnsInBeat : 0,
        turnsRequired: STORY_TURNS_PER_BEAT
      });
    }
    const lastImage = state.lastImage as { imageUrl?: unknown; sceneTitle?: unknown } | undefined;
    const configuredImage = state.initialImageUrl;
    if (lastImage && typeof lastImage.imageUrl === "string") {
      setSceneImage(lastImage.imageUrl);
      if (typeof lastImage.sceneTitle === "string") setVisualSceneTitle(lastImage.sceneTitle);
    }
    else if (typeof configuredImage === "string" && configuredImage.trim()) setSceneImage(configuredImage);
  }

  function resetGame() {
    recorderRef.current.stop();
    clearTalkHoldState();
    talkIntentRef.current = false;
    talkingRef.current = false;
    spaceDownRef.current = false;
    setIsPreparingToTalk(false);
    setIsTalking(false);
    playerRef.current?.reset();
    try {
      socketRef.current?.send({ type: "reset" });
    } catch {
      // The socket may already be closed; local state still needs to be reset.
    }
    socketRef.current?.close();
    socketRef.current = undefined;
    connectPromiseRef.current = undefined;
    realtimeReadyRef.current = false;
    setRealtimeReady(false);
    connectionSeqRef.current += 1;
    clearSavedDefaultAuth();
    playedRewardVideosRef.current = new Set();
    failedRewardVideosRef.current = new Set();
    setAuthToken("");
    setAuthExpiresAt(0);
    setPassword("");
    setSocketState("idle");
    setSceneImage(DEFAULT_INITIAL_IMAGE_URL);
    setSceneTitle(STORY_BEATS[0].title);
    setVisualSceneTitle(STORY_BEATS[0].title);
    setInputLocked(false);
    setInputLockReason("");
    setDirectorFeedback("");
    setChoicePrompt("");
    setTurnStage(undefined);
    setScenePhase("idle");
    setSceneTransitionText("");
    setSceneCamera(DEFAULT_SCENE_CAMERA);
    clearNarration();
    setStoryProgress(DEFAULT_STORY_PROGRESS);
    setUnlockedRewardVideos(new Set([STORY_BEATS[0].videoUrl]));
    setPlayedRewardVideos(new Set());
    setFailedRewardVideos(new Set());
    setActiveRewardVideo(undefined);
    setSettingsOpen(true);
    clearLiveUserTranscript();
    setLiveAssistantTranscript("");
    setTranscript([SEED_TRANSCRIPT]);
    setStatus("已重置，请重新登录");
  }

  const connectionText =
    socketState === "connected" ? "在线" : socketState === "connecting" ? "接入中" : socketState === "error" ? "异常" : "离线";
  const isConnecting = socketState === "connecting" || (socketState === "connected" && !realtimeReady);
  const connectButtonLabel = isConnecting ? "正在接入" : socketState === "connected" ? "重新连接" : "连接会话";
  const routeConfig = REALTIME_ROUTE_OPTIONS.find((option) => option.value === route) ?? REALTIME_ROUTE_OPTIONS[0];
  const routeVoiceOptions = getRealtimeVoicesForRoute(route);
  const imageProviderConfig = getImageProviderConfig(imageProvider);
  const needsLogin = !authToken;
  const compactVoiceName = getRealtimeVoiceConfig(voice).label.replace(/\s*(男声|女声).*$/u, "");
  const compactConnectionLabel =
    inputMode === "text"
      ? realtimeReady
        ? "在线 · 文字"
        : authToken
          ? "文字 · 待接入"
          : "文字 · 待登录"
      : realtimeReady
        ? route === "minicpm" && isDuplexListening
          ? "全双工监听"
          : `在线 · ${compactVoiceName}`
        : isConnecting || isPreparingToTalk
          ? "接入中"
          : authToken
            ? "待接入"
            : "待登录";
  const miniCpmDuplexInput = route === "minicpm" && isDuplexListening && inputMode === "voice";
  const compactTalkLabel = miniCpmDuplexInput ? (isTalking ? "松开回应" : "全双工") : inputLocked ? "按住打断" : isTalking ? "松开" : isPreparingToTalk ? "接入中" : "按住说话";
  const fullTalkLabel = miniCpmDuplexInput
    ? isTalking
      ? "松开让她回应"
      : inputLocked
        ? "按住打断"
        : "全双工监听"
    : inputLocked
      ? "按住打断"
      : isTalking
        ? "松开发送"
        : isPreparingToTalk
          ? "接入中..."
          : "按住说话";
  const talkButtonAriaLabel = miniCpmDuplexInput
    ? isTalking
      ? "松开让林夏回应"
      : "MiniCPM 全双工监听中，按住打断"
    : inputLocked
      ? "按住打断林夏并说话"
      : isTalking
        ? "松开发送"
        : isPreparingToTalk
          ? "正在接入语音链路"
          : "按住说话";
  const currentBeatIndex = getStoryBeatIndex(sceneTitle);
  const progressText =
    currentBeatIndex >= 0
      ? `${currentBeatIndex + 1}/${STORY_BEATS.length} · ${storyProgress.turnsInBeat}/${storyProgress.turnsRequired}`
      : "延展中";
  const sceneChapter = currentBeatIndex >= 0 ? `CHAPTER ${String(currentBeatIndex + 1).padStart(2, "0")}` : "CHAPTER +";
  const sceneDisplayTitle = visualSceneTitle || sceneTitle;
  const progressBeats: StoryBeat[] =
    currentBeatIndex >= 0
      ? STORY_BEATS
      : [
          ...STORY_BEATS,
          { title: sceneTitle, prompt: "", videoUrl: "", videoPrompt: "", videoDurationSeconds: 5, videoHasAudio: false }
        ];
  const rewardVideoUrl = activeRewardVideo?.videoUrl;
  const showRewardVideo = Boolean(rewardVideoUrl);
  const latestAssistantLine = [...transcript]
    .reverse()
    .find((line) => line.role === "assistant" && line.text.trim());
  const assistantCaptionText = liveAssistantTranscript || latestAssistantLine?.text.trim() || "";
  const readyChoicePrompt = !inputLocked && choicePrompt ? choicePrompt : "";
  const stageHintText = turnStage?.text || directorFeedback || (scenePhase !== "idle" ? sceneTransitionText : inputLocked ? inputLockReason : readyChoicePrompt);
  const stageHintLabel =
    turnStage?.label ||
    (directorFeedback
      ? "已听到"
      : scenePhase === "generating"
        ? "生成中"
        : scenePhase === "transition"
          ? "镜头"
          : inputLocked
            ? "演出中"
            : readyChoicePrompt
              ? "轮到你"
              : "现场");
  const stageHintPhase = turnStage ? getTurnStageHintPhase(turnStage.stage) : directorFeedback ? "feedback" : scenePhase !== "idle" ? scenePhase : inputLocked ? "locked" : readyChoicePrompt ? "ready" : "idle";
  const showLiveStageHint = conversationCollapsed && Boolean(stageHintText);
  const liveDialogueActive = Boolean(narration || assistantCaptionText || showLiveStageHint);
  const showLiveDialogueStack = conversationCollapsed && liveDialogueActive;
  const showPanelNarration = !conversationCollapsed && Boolean(narration);

  const shellClassName = `app-shell ${leftCollapsed ? "left-collapsed" : "left-open"} ${
    conversationCollapsed ? "conversation-collapsed" : "conversation-open"
  } ${showLiveDialogueStack ? "live-dialogue-active" : ""} ${inputLocked ? "input-locked" : ""} ${
    scenePhase !== "idle" ? "scene-active" : ""
  } ${narration ? "narration-active" : ""} camera-${sceneCamera.focus} mood-${sceneCamera.mood}`;

  if (needsLogin && !realtimeReady) {
    return (
      <main className={`${shellClassName} auth-only`}>
        <section className="scene-stage login-scene" aria-label="登录背景">
          <img
            className="scene-image"
            src={sceneImage}
            alt="当前游戏场景"
            draggable={false}
            onContextMenu={(event) => event.preventDefault()}
          />
          <div className="scene-vignette" />
          <section className="login-page-card" role="dialog" aria-modal="true" aria-labelledby="login-page-title">
            <div className="login-page-header">
              <span>DEFAULT KEY ACCESS</span>
              <h2 id="login-page-title">登录后进入配置</h2>
              <p>使用服务器配置的语音、剧情和生图密钥。登录成功后再选择技术路线与角色音色。</p>
            </div>
            <form className="login-page-form" onSubmit={handleLogin}>
              <label>
                用户名
                <input
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value);
                    setLoginError("");
                  }}
                  autoComplete="username"
                  autoFocus
                />
              </label>
              <label>
                密码
                <input
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setLoginError("");
                  }}
                  type="password"
                  autoComplete="current-password"
                />
              </label>
              {loginError && (
                <p className="login-error" role="alert">
                  {loginError}
                </p>
              )}
              <button className="primary-small" type="submit">
                <LogIn size={16} />
                进入配置
              </button>
            </form>
          </section>
        </section>
      </main>
    );
  }

  return (
    <main className={shellClassName}>
      <section className="scene-stage" aria-label="游戏场景">
        <img
          className="scene-image"
          src={sceneImage}
          alt="当前游戏场景"
          draggable={false}
          onContextMenu={(event) => event.preventDefault()}
        />
        {rewardVideoUrl && showRewardVideo && (
          <video
            key={`${activeRewardVideo?.videoUrl}:${activeRewardVideo?.playbackId}`}
            className="scene-video"
            src={rewardVideoUrl}
            aria-label={`${activeRewardVideo?.title ?? sceneTitle} 节点奖励影像`}
            autoPlay
            muted
            playsInline
            preload="auto"
            onContextMenu={(event) => event.preventDefault()}
            onEnded={() => markRewardVideoPlayed(rewardVideoUrl)}
            onError={() => markRewardVideoFailed(rewardVideoUrl)}
          />
        )}
        <div className="scene-vignette" />
        {scenePhase !== "idle" && (
          <div className="director-layer" aria-live="polite">
            <div className={`scene-status ${scenePhase}`}>
              <Waves size={15} />
              <span>{sceneTransitionText}</span>
            </div>
          </div>
        )}
        {showLiveDialogueStack && (
          <div className="live-dialogue-stack" aria-live="polite">
            {showLiveStageHint && (
              <div className={`live-stage-hint ${stageHintPhase}`}>
                <span>{stageHintLabel}</span>
                <p>{stageHintText}</p>
              </div>
            )}
            {narration && (
              <div className="director-caption" key={narration.id}>
                <span>{narration.title}</span>
                <p>{narration.text}</p>
              </div>
            )}
            {assistantCaptionText && (
              <div className={`live-caption live-assistant-caption ${assistantSpeaking ? "streaming" : ""}`}>
                <span>林夏</span>
                <p>{assistantCaptionText}</p>
              </div>
            )}
          </div>
        )}
        <div className="scene-topline">
          <span>
            <CircleDot size={12} />
            LIVE CALL
          </span>
          <strong>{getRealtimeVoiceConfig(voice).label}</strong>
        </div>
        <div className="scene-caption">
          <span>{sceneChapter}</span>
          <strong>{sceneDisplayTitle}</strong>
          <p>林夏靠在后门的冷风里，电话线另一端只剩你的声音。</p>
        </div>
      </section>

      <button
        className="panel-toggle panel-toggle-left"
        type="button"
        aria-label={leftCollapsed ? "展开左侧线索栏" : "收起左侧线索栏"}
        aria-expanded={!leftCollapsed}
        onClick={() => setLeftCollapsed((collapsed) => !collapsed)}
      >
        {leftCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
      </button>

      {rewardHint && (
        <button
          className="reward-hint"
          type="button"
          onClick={() => {
            const beat = STORY_BEATS.find((item) => item.videoUrl === rewardHint.videoUrl);
            if (beat) replayRewardVideo(beat, STORY_BEATS.indexOf(beat));
          }}
        >
          <PlayCircle size={18} />
          <span>新影像已解锁</span>
          <strong>{formatBeatTitle(rewardHint.title)}</strong>
          <em>{leftCollapsed ? "点击播放，或展开线索栏领取" : "点击左侧线索可播放"}</em>
        </button>
      )}

      <button
        className="panel-toggle panel-toggle-right"
        type="button"
        aria-label={conversationCollapsed ? "展开右侧通话栏" : "收起右侧通话栏"}
        aria-expanded={!conversationCollapsed}
        onClick={() => setConversationCollapsed((collapsed) => !collapsed)}
      >
        {conversationCollapsed ? <PanelRightOpen size={18} /> : <PanelRightClose size={18} />}
      </button>

      {conversationCollapsed && (
        <div className={`voice-float ${assistantSpeaking ? "assistant-speaking" : ""}`} aria-label="收起状态语音输入">
          <VoiceActivity active={assistantSpeaking} compact />
          <div className={`compact-signal ${realtimeReady ? "online" : isConnecting || isPreparingToTalk ? "connecting" : ""}`}>
            <CircleDot size={10} />
            <span>{compactConnectionLabel}</span>
          </div>
          {inputLocked && (
            <button className="skip-director-button compact-skip-button" type="button" onClick={() => skipDirectorPerformance()}>
              <SkipForward size={15} />
              跳过
            </button>
          )}
          {inputMode === "text" ? (
            <form className="compact-composer" onSubmit={sendText} aria-label="折叠状态文字输入">
              <input
                value={textInput}
                onChange={(event) => setTextInput(event.target.value)}
                disabled={inputLocked}
                placeholder={inputLocked ? "林夏正在观察现场" : "输入一句话"}
              />
              <button type="submit" aria-label="发送文字" disabled={inputLocked}>
                <Send size={18} />
              </button>
            </form>
          ) : (
            <button
              className={`talk-button compact-talk-button ${isTalking ? "recording" : ""} ${isPreparingToTalk ? "preparing" : ""}`}
              type="button"
              onContextMenu={(event) => event.preventDefault()}
              onPointerDown={handleTalkPointerDown}
              onPointerUp={handleTalkPointerUp}
              onPointerCancel={handleTalkPointerCancel}
              aria-label={talkButtonAriaLabel}
            >
              <Mic size={28} />
              <span>{compactTalkLabel}</span>
            </button>
          )}
        </div>
      )}

      <section className="left-rail" aria-hidden={leftCollapsed}>
        <div className="brand">
          <div className="brand-mark">
            <Sparkles size={22} />
          </div>
          <div>
            <h1>Whisperverse</h1>
            <p>LIN XIA SIGNAL</p>
          </div>
        </div>

        <div className="signal-card">
          <span className={`dot ${socketState === "connected" ? "online" : ""}`} />
          <div>
            <strong>{connectionText}</strong>
            <span>{status}</span>
          </div>
        </div>

        <div className="chapter-panel" aria-label="线索进度">
          <div className="rail-section-title">
            <span>线索进度</span>
            <strong>{progressText}</strong>
          </div>
          <div className="chapter-list">
            {progressBeats.map((beat, index) => {
              const isCurrent = beat.title === sceneTitle;
              const isKnownExtension = currentBeatIndex < 0 && index >= STORY_BEATS.length;
              const isRewardUnlocked = Boolean(beat.videoUrl) && unlockedRewardVideos.has(beat.videoUrl);
              const isSeen = isCurrent || isRewardUnlocked || isKnownExtension;
              const canReplayVideo = isRewardUnlocked && Boolean(beat.videoUrl);
              const hasPlayedVideo = playedRewardVideos.has(beat.videoUrl);
              const hasFailedVideo = failedRewardVideos.has(beat.videoUrl);
              return (
                <button
                  aria-label={canReplayVideo ? `播放${formatBeatTitle(beat.title)}奖励视频` : `${formatBeatTitle(beat.title)}未解锁`}
                  className={`chapter-item ${isCurrent ? "current" : ""} ${isSeen ? "seen" : ""} ${
                    canReplayVideo ? "playable" : ""
                  }`}
                  disabled={!canReplayVideo}
                  key={beat.title}
                  onClick={() => replayRewardVideo(beat, index)}
                  type="button"
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{formatBeatTitle(beat.title)}</strong>
                  <em>
                    {canReplayVideo ? (
                      <>
                        <PlayCircle size={13} />
                        {hasFailedVideo ? "重试" : hasPlayedVideo ? "重播" : "播放"}
                      </>
                    ) : isSeen ? (
                      "已解锁"
                    ) : (
                      "待发现"
                    )}
                  </em>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rail-utility-actions" aria-label="线索栏操作">
          <button type="button" onClick={redrawCurrentScene} aria-label="重绘画面">
            <Aperture size={16} />
          </button>
          <button type="button" onClick={() => setSettingsOpen(true)} aria-label="设置">
            <Settings size={16} />
          </button>
          <button type="button" onClick={resetGame} aria-label="重置">
            <RotateCcw size={16} />
          </button>
        </div>
      </section>

      <section className="conversation-panel" ref={conversationPanelRef} aria-hidden={conversationCollapsed}>
        <div className="panel-header">
          <div>
            <h2>林夏</h2>
            <p className="panel-status-line">
              <CircleDot size={9} />
              <span>
                {socketState === "connected" ? "语音链路待命" : "等待连接"} · {assistantSpeaking ? "林夏正在说话" : getRealtimeVoiceConfig(voice).label}
              </span>
              <VoiceActivity active={assistantSpeaking} compact />
            </p>
          </div>
          <details className="panel-actions-menu">
            <summary aria-label="面板操作">
              <MoreHorizontal size={20} />
            </summary>
            <div className="panel-actions-popover">
              <button type="button" onClick={() => setSettingsOpen(true)}>
                <Settings size={16} />
                设置
              </button>
              <button type="button" onClick={redrawCurrentScene}>
                <Aperture size={16} />
                重绘画面
              </button>
              <button type="button" onClick={resetGame}>
                <RotateCcw size={16} />
                重置
              </button>
            </div>
          </details>
        </div>

        <div className="transcript" aria-live="polite" ref={transcriptRef}>
          {showPanelNarration && narration && (
            <div className="panel-narration" key={narration.id}>
              <span>{narration.title}</span>
              <p>{narration.text}</p>
            </div>
          )}
          {transcript.map((line) => (
            <div className={`line ${line.role}`} key={line.id}>
              <span>{getLineSpeaker(line.role)}</span>
              <p>{line.text}</p>
            </div>
          ))}
        </div>

        <div className="call-control-stack">
          {(inputLocked || choicePrompt) && (
            <div className={`choice-hint ${inputLocked ? "locked" : ""}`} aria-live="polite">
              <div>
                <span>{inputLocked ? "演出中" : "轮到你"}</span>
                {inputLocked && directorFeedback && <em className="choice-feedback">{directorFeedback}</em>}
                <p>{inputLocked ? inputLockReason || "林夏正在观察现场" : choicePrompt}</p>
              </div>
              {inputLocked && (
                <button className="skip-director-button" type="button" onClick={() => skipDirectorPerformance()}>
                  <SkipForward size={14} />
                  跳过
                </button>
              )}
            </div>
          )}

          <div className="input-mode-switch" role="tablist" aria-label="输入方式">
            <button
              className={inputMode === "voice" ? "selected" : ""}
              type="button"
              role="tab"
              aria-selected={inputMode === "voice"}
              onClick={() => setInputMode("voice")}
            >
              <Mic size={16} />
              语音
            </button>
            <button
              className={inputMode === "text" ? "selected" : ""}
              type="button"
              role="tab"
              aria-selected={inputMode === "text"}
              onClick={() => setInputMode("text")}
            >
              <Send size={16} />
              文字
            </button>
          </div>

          {inputMode === "voice" ? (
            <div className="voice-pad">
              <button
                className={`talk-button ${isTalking ? "recording" : ""} ${isPreparingToTalk ? "preparing" : ""}`}
                type="button"
                onContextMenu={(event) => event.preventDefault()}
                onPointerDown={handleTalkPointerDown}
                onPointerUp={handleTalkPointerUp}
                onPointerCancel={handleTalkPointerCancel}
              >
                <Mic size={30} />
                <span>{fullTalkLabel}</span>
              </button>
              <div className="voice-actions">
                <span className="shortcut-chip">
                  <KeyboardIcon />
                  Space
                </span>
                <span className="shortcut-chip">
                  <Waves size={14} />
                  {miniCpmDuplexInput ? "16k Duplex" : route === "qwen-audio" ? "16k PCM" : "24k PCM"}
                </span>
              </div>
            </div>
          ) : (
            <form className="composer" onSubmit={sendText}>
              <input
                value={textInput}
                onChange={(event) => setTextInput(event.target.value)}
                disabled={inputLocked}
                placeholder={inputLocked ? "林夏正在观察现场" : "输入一句话"}
              />
              <button type="submit" aria-label="发送文字" disabled={inputLocked}>
                <Send size={18} />
              </button>
            </form>
          )}
        </div>
      </section>

      {settingsOpen && (
        <div
          className={`drawer-backdrop ${realtimeReady ? "" : "preconnect-backdrop"}`}
          role="presentation"
          onMouseDown={() => {
            if (realtimeReady) setSettingsOpen(false);
          }}
        >
          <aside
            className={`settings-drawer ${realtimeReady ? "" : "preconnect"}`}
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="drawer-header">
              <div>
                <span>{realtimeReady ? "CONNECTION" : "PRE-CALL SETUP"}</span>
                <h2>{realtimeReady ? "设置" : "选择技术路线"}</h2>
              </div>
              {realtimeReady && (
                <button className="text-button" type="button" onClick={() => setSettingsOpen(false)}>
                  完成
                </button>
              )}
            </div>

            <div className="settings-scroll">
              <div className="route-intro">
                <strong>{routeConfig.headline}</strong>
                <p>{routeConfig.description}</p>
              </div>

              <div className="route-option-grid" role="radiogroup" aria-label="技术路线">
                {REALTIME_ROUTE_OPTIONS.map((routeOption) => {
                  const selected = routeOption.value === route;
                  const voices = getRealtimeVoicesForRoute(routeOption.value);
                  return (
                    <button
                      aria-checked={selected}
                      className={`route-card ${selected ? "selected" : ""}`}
                      key={routeOption.value}
                      onClick={() => {
                        setRoute(routeOption.value);
                        setVoice(resolveRealtimeRouteVoice(routeOption.value, voice));
                        if (socketState === "connected") setStatus("技术路线会在重新连接后生效");
                      }}
                      role="radio"
                      type="button"
                    >
                      <span>{routeOption.badge}</span>
                      <strong>{routeOption.label}</strong>
                      <em>{routeOption.headline}</em>
                      <p>{routeOption.description}</p>
                      <small>{voices.map((voiceOption) => voiceOption.label.replace(/\s*\(.+?\)/u, "")).join(" / ")}</small>
                    </button>
                  );
                })}
              </div>

              <label className="settings-field">
                {route === "qwen-audio" ? "实时模型" : "角色音色"} · {routeConfig.label}
                <select
                  value={voice}
                  onChange={(event) => {
                    const nextVoice = event.target.value as RealtimeVoice;
                    setVoice(nextVoice);
                    setRoute(getRealtimeRouteForVoice(nextVoice));
                    if (socketState === "connected") setStatus("角色音色会在重新连接后生效");
                  }}
                >
                  {routeVoiceOptions.map((voiceOption) => (
                    <option key={voiceOption.value} value={voiceOption.value}>
                      {voiceOption.label}
                    </option>
                  ))}
                </select>
              </label>

              <section className="image-provider-section" aria-labelledby="image-provider-title">
                <div className="section-kicker">
                  <span id="image-provider-title">生图供应商</span>
                  <strong>{imageProviderConfig.label}</strong>
                </div>
                <div className="image-provider-grid" role="radiogroup" aria-label="生图供应商">
                  {IMAGE_PROVIDER_OPTIONS.map((providerOption) => {
                    const selected = providerOption.value === imageProvider;
                    return (
                      <button
                        aria-checked={selected}
                        className={`image-provider-card ${selected ? "selected" : ""}`}
                        key={providerOption.value}
                        onClick={() => {
                          setImageProvider(providerOption.value);
                          if (socketState === "connected") setStatus("生图供应商会在重新连接后生效");
                        }}
                        role="radio"
                        type="button"
                      >
                        <span>{providerOption.badge}</span>
                        <strong>{providerOption.label}</strong>
                        <em>{providerOption.headline}</em>
                        <p>{providerOption.description}</p>
                      </button>
                    );
                  })}
                </div>
              </section>

              <label className="settings-toggle">
                <span>
                  <strong>聊天记录包含旁白</strong>
                  <em>默认关闭，旁白只显示在画面浮层。</em>
                </span>
                <input
                  checked={showNarrationInTranscript}
                  onChange={(event) => setShowNarrationInTranscript(event.target.checked)}
                  type="checkbox"
                />
              </label>

              {authToken ? (
                <div className="server-login-box logged-in">
                  <div>
                    <strong>已授权</strong>
                    <span>有效至 {formatAuthExpiry(authExpiresAt)}</span>
                  </div>
                  <span className="auth-chip">已登录</span>
                </div>
              ) : realtimeReady ? (
                <form className="settings-login-form" onSubmit={handleLogin}>
                  <div>
                    <strong>登录授权</strong>
                    <span>登录后启用服务器配置的密钥</span>
                  </div>
                  <label>
                    用户名
                    <input
                      value={username}
                      onChange={(event) => {
                        setUsername(event.target.value);
                        setLoginError("");
                      }}
                      autoComplete="username"
                    />
                  </label>
                  <label>
                    密码
                    <input
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setLoginError("");
                      }}
                      type="password"
                      autoComplete="current-password"
                    />
                  </label>
                  {loginError && (
                    <p className="login-error" role="alert">
                      {loginError}
                    </p>
                  )}
                  <button className="primary-small" type="submit">
                    <LogIn size={16} />
                    登录授权
                  </button>
                </form>
              ) : null}
            </div>

            {authToken && (
              <div className="settings-footer">
                <button
                  className="connect-button"
                  type="button"
                  onClick={() => {
                    void connect();
                  }}
                  disabled={isConnecting}
                >
                  <Power size={18} />
                  {connectButtonLabel}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </main>
  );
}

function KeyboardIcon() {
  return <span className="keyboard-mark">⌘</span>;
}

function getTurnStageHintPhase(stage: TurnStageName) {
  switch (stage) {
    case "visualizing":
      return "generating";
    case "framing":
      return "transition";
    case "ready":
      return "ready";
    case "heard":
    case "speaking":
      return "feedback";
  }
}

function VoiceActivity({ active, compact = false }: { active: boolean; compact?: boolean }) {
  return (
    <span
      className={`assistant-voice-activity ${active ? "active" : ""} ${compact ? "compact" : ""}`}
      aria-hidden={!active}
    >
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function getLineSpeaker(role: TranscriptLine["role"]) {
  if (role === "assistant") return "林夏";
  if (role === "user") return "玩家";
  if (role === "narration") return "旁白";
  return "系统";
}

function mergeLiveTranscriptDelta(current: string, delta: string) {
  const cleanDelta = delta.trim();
  if (!cleanDelta) return current;
  if (!current) return cleanDelta;
  if (cleanDelta.startsWith(current)) return cleanDelta;
  if (current.endsWith(cleanDelta)) return current;
  return `${current}${cleanDelta}`;
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return tagName === "input" || tagName === "textarea" || target.isContentEditable;
}

function describeSceneForImage(sceneTitle: string) {
  return getStoryBeatPrompt(sceneTitle);
}

function formatBeatTitle(title: string) {
  return title.replace(/^超市/, "");
}

function isNarrowViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 720px)").matches;
}

function readSavedDefaultAuth(): SavedDefaultAuth | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<SavedDefaultAuth>;
    if (typeof parsed.token !== "string" || typeof parsed.username !== "string" || typeof parsed.expiresAt !== "number") {
      clearSavedDefaultAuth();
      return undefined;
    }
    if (!isSavedAuthActive(parsed.expiresAt)) {
      clearSavedDefaultAuth();
      return undefined;
    }
    return { token: parsed.token, username: parsed.username, expiresAt: parsed.expiresAt };
  } catch {
    clearSavedDefaultAuth();
    return undefined;
  }
}

function writeSavedDefaultAuth(auth: SavedDefaultAuth) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
}

function clearSavedDefaultAuth() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
}

function readNarrationTranscriptPreference() {
  if (typeof window === "undefined") return false;
  try {
    return parseNarrationTranscriptPreference(window.localStorage.getItem(NARRATION_TRANSCRIPT_STORAGE_KEY));
  } catch {
    return false;
  }
}

function writeNarrationTranscriptPreference(value: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(NARRATION_TRANSCRIPT_STORAGE_KEY, serializeNarrationTranscriptPreference(value));
}

function isSavedAuthActive(expiresAt: number) {
  return Number.isFinite(expiresAt) && expiresAt > Math.floor(Date.now() / 1000) + 5;
}

function formatAuthExpiry(expiresAt: number) {
  if (!isSavedAuthActive(expiresAt)) return "已过期";
  return new Date(expiresAt * 1000).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}
